// "Gravar a Ficha" (issue #91): o "Salvar" e o "Fechar OS" da Ficha da OS
// numa operação só. A tela manda a Ficha inteira como foi digitada e a
// intenção; o domínio escolhe o caminho pela situação da OS e grava Coleta e
// Fechamento juntos. Como é uma transição pura do Snapshot, uma recusa não
// deixa nada gravado pela metade. Antes, a tela decidia entre quatro funções
// (e o "Fechar OS" com a Coleta desatualizada virava dois comandos).

import type { DataISO } from "./datas.ts";
import type { Id } from "./ids.ts";
import {
  guardarRascunhoFechamento,
  reabrirFechamento,
  registrarDevolucao,
  salvarFechamentoEmCorrecao,
  type DadosDevolucao,
  type LinhaDevolucao,
} from "./devolucao.ts";
import { emCorrecao } from "./fechamento-do-lote.ts";
import { contagensDoLote, editarLote, ErroLote, situacaoLote, type DadosLote } from "./lotes.ts";
import type { RascunhoFechamento, Snapshot } from "./snapshot.ts";

export type IntencaoFicha = "salvar" | "fechar";

/** A Ficha da OS como foi digitada: Coleta e Fechamento. */
export interface FichaGravada {
  dados: DadosLote;
  /** A Comanda inteira: Recebida e Retida digitada de cada Tipo de Peça. */
  recebidas: { tipoId: Id; quantidade: number; retidaInformada?: number | null }[];
  fechamento: { data: string; hora?: string; linhas: LinhaDevolucao[] };
  /**
   * Reabrir Fechamento: numa OS fechada, o "Salvar" reabre a OS (fica
   * Lançada) e grava as correções do Fechamento. Na tela, é o botão "Reabrir
   * Fechamento" clicado antes do "Salvar".
   */
  reabrirFechamento: boolean;
}

/** A Coleta digitada difere da gravada (cabeçalho, Recebida ou Retida digitada)? */
function coletaMudou(snapshot: Snapshot, loteId: Id, ficha: FichaGravada): boolean {
  const lote = snapshot.lotes.find((l) => l.id === loteId)!;
  const d = ficha.dados;
  if (
    d.clienteId !== lote.clienteId || d.os !== lote.os || d.coletaData !== lote.coletaData ||
    d.coletaHora !== lote.coletaHora || d.paradaId !== lote.paradaId || d.sacos !== lote.sacos ||
    d.urgente !== lote.urgente || d.relavagem !== lote.relavagem || d.previstoManual !== lote.previstoManual
  ) {
    return true;
  }
  const gravadas = new Map(contagensDoLote(snapshot, loteId).map((c) => [c.tipoId, c]));
  const digitadas = new Map(ficha.recebidas.map((r) => [r.tipoId, r]));
  for (const [tipoId, c] of gravadas) {
    if (c.recebida > 0 && !digitadas.has(tipoId)) return true;
  }
  for (const r of ficha.recebidas) {
    const c = gravadas.get(r.tipoId);
    if ((c?.recebida ?? 0) !== r.quantidade || (c?.retidaInformada ?? null) !== (r.retidaInformada ?? null)) return true;
  }
  return false;
}

function comoRascunho(f: FichaGravada["fechamento"]): RascunhoFechamento {
  return {
    data: f.data,
    hora: f.hora || null,
    linhas: f.linhas.map((l) => ({
      tipoId: l.tipoId,
      devolvida: l.devolvida,
      retidas: l.retidas.map((r) => ({ ...r })),
      conferida: l.conferida ?? null,
      conferidaMotivo: l.conferidaMotivo ?? null,
    })),
  };
}

/**
 * Grava a Ficha da OS. Pela situação da OS e pela intenção:
 * - **fechar**: grava a Coleta (se mudou) e fecha — confere a retenção e exige
 *   a hora (ADR-0029, ADR-0028); o carimbo de registro só no primeiro Fechar.
 * - **salvar**, OS lançada: grava a Coleta e o Rascunho do Fechamento, sem
 *   conferir a retenção.
 * - **salvar**, OS fechada com o Fechamento destravado: Reabrir Fechamento —
 *   a OS fica Lançada e as correções são gravadas.
 * - **salvar**, OS reaberta para correção: grava as correções sem fechar.
 * - **salvar**, OS fechada sem destravar: só a Coleta.
 */
export function gravarFicha(
  snapshot: Snapshot,
  loteId: Id,
  ficha: FichaGravada,
  intencao: IntencaoFicha,
  contexto: { hoje: DataISO; agora: string },
): Snapshot {
  const lote = snapshot.lotes.find((l) => l.id === loteId);
  if (!lote) throw new ErroLote("Lote não encontrado.");
  const situacao = situacaoLote(snapshot, loteId);
  const jaFechou = situacao === "fechado" || situacao === "reaberta";
  const fechamento: DadosDevolucao = {
    data: ficha.fechamento.data,
    ...(ficha.fechamento.hora ? { hora: ficha.fechamento.hora } : {}),
    linhas: ficha.fechamento.linhas,
  };

  if (intencao === "fechar") {
    const comColeta = coletaMudou(snapshot, loteId, ficha)
      ? editarLote(snapshot, loteId, ficha.dados, ficha.recebidas)
      : snapshot;
    return registrarDevolucao(comColeta, loteId, fechamento, {
      conferirRetencao: true,
      exigirHora: true,
      registradoEm: contexto.agora,
      hoje: contexto.hoje,
    });
  }

  if (jaFechou && ficha.reabrirFechamento) {
    const reaberta = editarLote(reabrirFechamento(snapshot, loteId), loteId, ficha.dados, ficha.recebidas);
    return salvarFechamentoEmCorrecao(reaberta, loteId, fechamento);
  }
  const editado = editarLote(snapshot, loteId, ficha.dados, ficha.recebidas);
  if (situacao === "lancado" && emCorrecao(lote)) return salvarFechamentoEmCorrecao(editado, loteId, fechamento);
  if (situacao === "lancado") return guardarRascunhoFechamento(editado, loteId, comoRascunho(ficha.fechamento));
  return editado;
}
